import { useState } from "react";
import { LeagueDisplay } from "./LeagueDisplay";
import {
  compareLeagueSelection,
  getLeagueCountryName,
} from "../helpers/leagueSelection";
import { TIP_MARKET_OPTIONS } from "../helpers/tipSelection";
import { weekSection, adjacentSection } from "../helpers/gamesCalendar";
import "./TipsSelection.css";
export default function TipsSelection({
  value,
  onChange,
  seasons = [],
  leagues = [],
  bookmakers = [],
  bookmakerError,
  period,
  setPeriod,
  customRange,
  setCustomRange,
  busy,
  onFind,
  onSave,
  t,
}) {
  const [search, setSearch] = useState(""),
    [bookSearch, setBookSearch] = useState("");
  const set = (key, v) => onChange({ ...value, [key]: v });
  const options = seasons
    .map((s) => ({
      ...leagues.find((l) => Number(l.id) === s.league_id),
      id: s.league_id,
      name: s.league_name,
      country:
        s.country || leagues.find((l) => Number(l.id) === s.league_id)?.country,
      image_path:
        s.image_path ||
        leagues.find((l) => Number(l.id) === s.league_id)?.image_path,
    }))
    .sort(compareLeagueSelection);
  const groups = new Map();
  for (const l of options.filter((l) =>
    `${l.name} ${getLeagueCountryName(l)}`
      .toLowerCase()
      .includes(search.toLowerCase())
  )) {
    const country = getLeagueCountryName(l);
    if (!groups.has(country)) groups.set(country, []);
    groups.get(country).push(l);
  }
  const ids = (key, all) =>
    value[key] === null ? all.map((v) => v.id) : value[key] || [];
  const toggle = (key, id, all, checked) =>
    set(
      key,
      checked
        ? [...new Set([...ids(key, all), id])]
        : ids(key, all).filter((x) => x !== id)
    );
  const current = weekSection(),
    next = adjacentSection(current, 1);
  const allButtons = (key, all) => (
    <div className="ts-bulk">
      <button
        type="button"
        onClick={() =>
          set(
            key,
            all.map((v) => v.id)
          )
        }
      >
        {t("workspace.all")}
      </button>
      <button type="button" onClick={() => set(key, [])}>
        {t("workspace.none")}
      </button>
      <span>
        {t("workspace.selected", {
          count: all.filter((x) => ids(key, all).includes(x.id)).length,
          total: all.length,
        })}
      </span>
    </div>
  );
  return (
    <fieldset disabled={busy} className="tips-selection">
      <legend>{t("start")}</legend>
      <div className="ts-period">
        <label>
          {t("time")}
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {[
              ["current-section", current],
              ["next-section", next],
            ].map(([key, s]) => (
              <option key={key} value={key}>
                {t(key, { week: s.week, section: s.section })}
              </option>
            ))}
            {["today", "6h", "24h", "2d", "3d", "7d", "14d"].map((k) => (
              <option key={k} value={k}>
                {t(k)}
              </option>
            ))}
            <option value="custom">{t("workspace.custom")}</option>
          </select>
        </label>
        {period === "custom" && (
          <>
            <label>
              {t("workspace.from")}
              <input
                type="date"
                value={customRange.from}
                onChange={(e) =>
                  setCustomRange({ ...customRange, from: e.target.value })
                }
              />
            </label>
            <label>
              {t("workspace.to")}
              <input
                type="date"
                value={customRange.to}
                onChange={(e) =>
                  setCustomRange({ ...customRange, to: e.target.value })
                }
              />
            </label>
          </>
        )}
      </div>
      <div className="ts-columns">
        <section>
          <h3>{t("leagues")}</h3>
          {allButtons("leagueIds", options)}
          <input
            aria-label={t("workspace.leagueSearch")}
            placeholder={t("workspace.leagueSearch")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="ts-list">
            {[...groups].map(([country, items]) => (
              <div key={country}>
                <h4>{country}</h4>
                {items.map((l) => (
                  <label className="ts-check" key={l.id}>
                    <input
                      type="checkbox"
                      checked={ids("leagueIds", options).includes(l.id)}
                      onChange={(e) =>
                        toggle("leagueIds", l.id, options, e.target.checked)
                      }
                    />
                    <LeagueDisplay league={l} />
                  </label>
                ))}
              </div>
            ))}
          </div>
        </section>
        <section>
          <h3>{t("bookmakers")}</h3>
          {allButtons("bookmakerIds", bookmakers)}
          <input
            aria-label={t("workspace.bookSearch")}
            placeholder={t("workspace.bookSearch")}
            value={bookSearch}
            onChange={(e) => setBookSearch(e.target.value)}
          />
          {bookmakerError && <p role="alert">{t("workspace.bookError")}</p>}
          <div className="ts-list">
            {bookmakers
              .filter((b) =>
                b.name.toLowerCase().includes(bookSearch.toLowerCase())
              )
              .map((b) => (
                <label className="ts-check" key={b.id}>
                  <input
                    type="checkbox"
                    checked={ids("bookmakerIds", bookmakers).includes(b.id)}
                    onChange={(e) =>
                      toggle("bookmakerIds", b.id, bookmakers, e.target.checked)
                    }
                  />
                  {b.image_path && <img src={b.image_path} alt="" />}
                  {b.name}
                </label>
              ))}
          </div>
        </section>
        <section>
          <h3>{t("markets")}</h3>
          {TIP_MARKET_OPTIONS.map((m) => (
            <label
              className="ts-check"
              key={m.id}
              title={t(`workspace.markets.${m.id}.help`)}
            >
              <input
                type="checkbox"
                disabled={!m.available}
                checked={m.available && (value.markets || []).includes(m.id)}
                onChange={(e) =>
                  set(
                    "markets",
                    e.target.checked
                      ? [...(value.markets || []), m.id]
                      : (value.markets || []).filter((v) => v !== m.id)
                  )
                }
              />
              <span>
                {t(`workspace.markets.${m.id}.name`)}
                {!m.available && <small> · {t("workspace.unavailable")}</small>}
              </span>
            </label>
          ))}
          <div className="ts-numbers">
            {["threshold", "minOdd", "maxOdd"].map((k) => (
              <label key={k} title={t("workspace.oddsHelp")}>
                {t(k)}
                <input
                  type="number"
                  step="0.01"
                  min={k === "threshold" ? 0 : 1.01}
                  placeholder={t("workspace.unlimited")}
                  value={
                    k === "minOdd" && value[k] === 1.01 ? "" : value[k] ?? ""
                  }
                  onChange={(e) =>
                    set(
                      k,
                      e.target.value === ""
                        ? k === "minOdd"
                          ? 1.01
                          : k === "threshold"
                          ? 0
                          : null
                        : Number(e.target.value)
                    )
                  }
                />
              </label>
            ))}
          </div>
        </section>
      </div>
      <details>
        <summary>{t("workspace.options")}</summary>
        <p>{t("workspace.stakeHelp")}</p>
        <div className="ts-numbers">
          {[
            "budget",
            "stake",
            "maxStake",
            "maxTotalStake",
            "combinationStake",
          ].map((k) => (
            <label key={k}>
              {t(k)}
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={value[k] ?? ""}
                onChange={(e) =>
                  set(k, e.target.value === "" ? null : Number(e.target.value))
                }
              />
            </label>
          ))}
        </div>
        <div className="ts-bulk">
          {["single", "accumulator", "system"].map((k) => (
            <label key={k}>
              <input
                type="checkbox"
                checked={(value.betTypes || []).includes(k)}
                onChange={(e) =>
                  set(
                    "betTypes",
                    e.target.checked
                      ? [...(value.betTypes || []), k]
                      : (value.betTypes || []).filter((v) => v !== k)
                  )
                }
              />
              {t(k)}
            </label>
          ))}
        </div>
        <div className="ts-numbers">
          {["systemMin", "systemMax"].map((k) => (
            <label key={k}>
              {t(k)}
              {["n", "m"].map((field) => (
                <input
                  key={field}
                  aria-label={`${t(k)} ${field}`}
                  type="number"
                  min="2"
                  max="20"
                  value={value[k]?.[field] ?? 2}
                  onChange={(e) =>
                    set(k, { ...value[k], [field]: Number(e.target.value) })
                  }
                />
              ))}
            </label>
          ))}
        </div>
      </details>
      <div className="ts-actions">
        <button
          type="button"
          className="bs-primary"
          disabled={
            bookmakerError ||
            !ids("leagueIds", options).length ||
            !ids("bookmakerIds", bookmakers).length ||
            !(value.markets || []).length
          }
          onClick={onFind}
        >
          {t("start")}
        </button>
        <button type="button" className="ts-save" onClick={() => onSave(value)}>
          {t("workspace.save")}
        </button>
      </div>
    </fieldset>
  );
}
